import React, { useState } from "react";
import {
  FaSearch,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaUsers,
} from "react-icons/fa";

const RoomBookingSearchForm = ({ onSearch }) => {
  const [formData, setFormData] = useState({
    address: "",
    guests: "",
    checkIn: "",
    checkOut: "",
  });

  const [errors, setErrors] = useState({});

  // Get today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split("T")[0];

  // Get tomorrow's date as minimum checkout
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split("T")[0];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Address validation
    if (!formData.address.trim()) {
      newErrors.address = "Please enter a destination or address";
    }

    // Guests validation
    if (!formData.guests || parseInt(formData.guests) < 1) {
      newErrors.guests = "Number of guests must be at least 1";
    }

    // Check-in date validation
    if (!formData.checkIn) {
      newErrors.checkIn = "Please select check-in date";
    } else if (formData.checkIn < today) {
      newErrors.checkIn = "Check-in date cannot be in the past";
    }

    // Check-out date validation
    if (!formData.checkOut) {
      newErrors.checkOut = "Please select check-out date";
    } else if (formData.checkOut <= formData.checkIn) {
      newErrors.checkOut = "Check-out date must be after check-in date";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRoomSearch = (e) => {
    e.preventDefault();

    if (validateForm()) {
      const searchData = {
        address: formData.address.trim(),
        guests: parseInt(formData.guests),
        checkIn: formData.checkIn,
        checkOut: formData.checkOut,
      };

      // Call parent handler
      if (onSearch) {
        onSearch(searchData);
      }

    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-800 mb-2">
          Find Your Perfect Room
        </h2>
        <p className="text-gray-600">
          Search and book comfortable accommodations for your stay
        </p>
      </div>

      {/* Search Form */}
      <form
        onSubmit={handleRoomSearch}
        className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Address/Destination Input */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <FaMapMarkerAlt className="inline mr-2 text-blue-500" />
              Destination or Address
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder="Enter city, hotel name, or address"
              className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.address
                  ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                  : "border-gray-200 focus:border-blue-400 hover:border-gray-300"
              }`}
            />
            {errors.address && (
              <p className="text-red-500 text-sm mt-1">{errors.address}</p>
            )}
          </div>

          {/* Number of Guests */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <FaUsers className="inline mr-2 text-green-500" />
              Guests
            </label>
            <input
              type="number"
              name="guests"
              min="1"
              max="10"
              value={formData.guests}
              onChange={handleInputChange}
              placeholder="No. of guests"
              className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-green-500 ${
                errors.guests
                  ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                  : "border-gray-200 focus:border-green-400 hover:border-gray-300"
              }`}
            />
            {errors.guests && (
              <p className="text-red-500 text-sm mt-1">{errors.guests}</p>
            )}
          </div>

          {/* Check-in Date */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <FaCalendarAlt className="inline mr-2 text-purple-500" />
              Check-in
            </label>
            <input
              type="date"
              name="checkIn"
              min={today}
              value={formData.checkIn}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                errors.checkIn
                  ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                  : "border-gray-200 focus:border-purple-400 hover:border-gray-300"
              }`}
            />
            {errors.checkIn && (
              <p className="text-red-500 text-sm mt-1">{errors.checkIn}</p>
            )}
          </div>

          {/* Check-out Date */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <FaCalendarAlt className="inline mr-2 text-orange-500" />
              Check-out
            </label>
            <input
              type="date"
              name="checkOut"
              min={formData.checkIn || tomorrowStr}
              value={formData.checkOut}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                errors.checkOut
                  ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                  : "border-gray-200 focus:border-orange-400 hover:border-gray-300"
              }`}
            />
            {errors.checkOut && (
              <p className="text-red-500 text-sm mt-1">{errors.checkOut}</p>
            )}
          </div>
        </div>

        {/* Search Button */}
        <div className="mt-8 text-center">
          <button
            type="submit"
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-4 px-8 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 flex items-center justify-center mx-auto min-w-48"
          >
            <FaSearch className="mr-3" />
            Search Rooms
          </button>
        </div>

        {/* Additional Info */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>
            ✨ Instant booking confirmation • 🔒 Secure payment • 📱 Digital
            receipt
          </p>
        </div>
      </form>
    </div>
  );
};

export default RoomBookingSearchForm;
